'use client'

import { LoginButton } from '@/components/auth'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useGlobalSearch } from '@/hooks/use-global-search'
import { cn } from '@/lib/utils'
import { useModalStore } from '@/stores/modal-store'
import { useUser } from '@account-kit/react'
import { ChevronDown, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { LanguagePicker } from '../language-picker'
import { BalanceDisplay } from './balance-display'
import { MobileMenu } from './mobile-menu'
import { MobileSearch } from './mobile-search'

export const navItemKeys = [
	'trade',
	'analytics',
	'trending',
	'activity',
	'topTraders',
	'smartMoney',
	'portfolio',
	'earn',
] as const

// Main nav items (non-dropdown)
export const navItems = [
	{ key: 'trade' as const, href: '/' },
	{ key: 'portfolio' as const, href: '/portfolio' },
	{ key: 'earn' as const, href: '/earn', icon: '/diamond.svg' },
]

// Analytics dropdown items
export const analyticsItems = [
	{ key: 'topTraders' as const, href: '/top-traders' },
	{ key: 'smartMoney' as const, href: '/smart-money' },
	{ key: 'activity' as const, href: '/activity' },
]

export function Header() {
	const [mobileSearchExpanded, setMobileSearchExpanded] = useState(false)
	const user = useUser()
	const { openModal } = useModalStore()
	const pathname = usePathname()
	const isTradePage = pathname.includes('/v2/')
	const t = useTranslations()

	const { searchValue, handleSearchChange, handleSearchKeyDown } = useGlobalSearch()

	return (
		<header className="sticky top-0 z-50 bg-brand-dark">
			<div
				className={cn(
					'relative mx-auto flex max-w-[1440px] items-center justify-between px-[16px] py-[24px]',
					isTradePage ? 'pt-[24px] pb-[10px]' : 'py-[24px]'
				)}
			>
				{/* Logo and nav - hidden on mobile when search is expanded */}
				<div
					className={cn(
						'flex min-w-0 flex-1 items-center gap-[8px] min-[1460px]:gap-[20px]',
						mobileSearchExpanded && 'hidden md:flex'
					)}
				>
					<a href="/" className="shrink-0">
						<Image
							src="/logos/logo.png"
							alt="OnAutopilot"
							width={120}
							height={32}
							className="h-8 w-auto"
						/>
					</a>
					<nav className="scrollbar-hide hidden min-w-0 flex-1 overflow-x-auto lg:flex">
						<div className="flex items-center whitespace-nowrap">
							{/* Trade link */}
							<Link
								href="/"
								className={cn(
									'flex shrink-0 items-center gap-[10px] px-[10px] text-[15px] leading-normal min-[1460px]:px-[16px]',
									pathname === '/'
										? 'font-bold text-white'
										: 'font-normal text-white/75 hover:text-white'
								)}
							>
								{t('nav.trade')}
							</Link>

							{/* Analytics dropdown */}
							<DropdownMenu>
								<DropdownMenuTrigger
									className={cn(
										'flex shrink-0 items-center gap-[6px] px-[10px] text-[15px] leading-normal outline-none min-[1460px]:px-[16px]',
										analyticsItems.some(item => pathname.startsWith(item.href))
											? 'font-bold text-white'
											: 'font-normal text-white/75 hover:text-white'
									)}
								>
									{t('nav.analytics')}
									<ChevronDown className="h-4 w-4" />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" className="border-white/10 bg-[#101215]">
									{analyticsItems.map(item => (
										<DropdownMenuItem key={item.key} asChild>
											<Link
												href={item.href}
												className={cn(
													'cursor-pointer text-[14px]',
													pathname.startsWith(item.href) ? 'font-bold text-white' : 'text-white/75'
												)}
											>
												{t(`nav.${item.key}`)}
											</Link>
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>

							{/* Portfolio link */}
							<Link
								href="/portfolio"
								className={cn(
									'flex shrink-0 items-center gap-[10px] px-[10px] text-[15px] leading-normal min-[1460px]:px-[16px]',
									pathname.startsWith('/portfolio')
										? 'font-bold text-white'
										: 'font-normal text-white/75 hover:text-white'
								)}
							>
								{t('nav.portfolio')}
							</Link>

							{/* Earn link */}
							<Link
								href="/earn"
								className={cn(
									'flex shrink-0 items-center gap-[10px] px-[10px] text-[15px] leading-normal min-[1460px]:px-[16px]',
									pathname.startsWith('/earn')
										? 'font-bold text-white'
										: 'font-normal text-white/75 hover:text-white'
								)}
							>
								<Image
									src="/diamond.svg"
									alt=""
									width={20}
									height={20}
									className="h-[20px] w-[20px]"
								/>
								{t('nav.earn')}
							</Link>
						</div>
					</nav>
				</div>

				<div
					className={cn(
						'flex shrink-0 items-center gap-[6px] min-[1400px]:gap-[12px]',
						mobileSearchExpanded ? 'w-full' : ''
					)}
				>
					{/* Mobile menu button - visible on mobile only */}
					<MobileMenu mobileSearchExpanded={mobileSearchExpanded} />

					{/* Mobile search - expands to fill header */}
					<MobileSearch
						isExpanded={mobileSearchExpanded}
						onExpand={() => setMobileSearchExpanded(true)}
						onCollapse={() => setMobileSearchExpanded(false)}
						searchValue={searchValue}
						onSearchChange={handleSearchChange}
						onSearchKeyDown={handleSearchKeyDown}
					/>

					{/* Desktop search button */}
					<div className="relative hidden md:block">
						<div className="flex h-[40px] items-center gap-[6px] rounded-[16px] border border-[#2a2a2a] bg-[#101215] pr-[16px] pl-[12px]">
							<Search className="h-[20px] w-[20px] text-white" />
							<Input
								placeholder={t('common.search')}
								value={searchValue}
								onChange={e => handleSearchChange(e.target.value)}
								onKeyDown={handleSearchKeyDown}
								className="h-auto min-h-0 w-[60px] border-0 bg-transparent p-0 font-normal text-[14px] text-white outline-none ring-0 placeholder:text-white focus:outline-none focus:ring-0 focus-visible:ring-0"
							/>
						</div>
					</div>

					{/* Deposit button */}
					{user ? (
						<>
							<Button
								onClick={() => openModal('deposit-primary')}
								className={cn(
									'h-[40px] cursor-pointer rounded-[16px] bg-brand-blue px-[16px] font-medium text-[14px] text-white leading-normal hover:bg-brand-blue/90',
									mobileSearchExpanded && 'hidden md:flex'
								)}
							>
								{t('header.deposit')}
							</Button>

							<Link
								href="/referrals"
								className={cn(
									'hidden h-[40px] cursor-pointer items-center rounded-[16px] bg-[#0A00BD40] px-[16px] font-medium text-[14px] text-white leading-normal hover:bg-[#0A00BD60] min-[1200px]:flex',
									mobileSearchExpanded && 'hidden lg:flex'
								)}
							>
								{t('header.referrals')}
							</Link>

							<BalanceDisplay />
						</>
					) : null}

					<div className="hidden md:block">
						<LanguagePicker />
					</div>

					<div className={cn(mobileSearchExpanded && 'hidden md:block')}>
						<LoginButton />
					</div>
				</div>
			</div>
		</header>
	)
}
